import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Gamepad2, Trophy, Search, Blocks, Bug, Pickaxe, CheckCircle, Link, Shield, Zap, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Games = () => {
  const navigate = useNavigate();

  const games = [
    {
      id: 'matching',
      title: 'Matching Game',
      description: 'Match blockchain terms with their correct definitions',
      icon: Link,
      path: '/game/matching',
      color: 'from-primary/20 to-transparent'
    },
    {
      id: 'quiz-battle',
      title: 'Quiz Battle',
      description: 'Race against time in this competitive quiz challenge',
      icon: Trophy,
      path: '/game/quiz-battle',
      color: 'from-yellow-500/20 to-transparent'
    },
    {
      id: 'detective',
      title: 'Blockchain Detective',
      description: 'Solve transaction mysteries and find the truth',
      icon: Search,
      path: '/game/detective',
      color: 'from-blue-500/20 to-transparent'
    },
    {
      id: 'builder',
      title: 'Blockchain Builder',
      description: 'Build a valid blockchain by connecting blocks',
      icon: Blocks,
      path: '/game/builder',
      color: 'from-purple-500/20 to-transparent'
    },
    {
      id: 'debugger',
      title: 'Smart Contract Debugger',
      description: 'Find and fix bugs in smart contract code',
      icon: Bug,
      path: '/game/debugger',
      color: 'from-red-500/20 to-transparent'
    },
    {
      id: 'mining',
      title: 'Hash Mining Simulator',
      description: 'Experience the process of mining and finding valid hashes',
      icon: Pickaxe,
      path: '/game/mining',
      color: 'from-orange-500/20 to-transparent'
    },
    {
      id: 'validator',
      title: 'Transaction Validator',
      description: 'Validate or reject transactions based on rules',
      icon: CheckCircle,
      path: '/game/validator',
      color: 'from-green-500/20 to-transparent'
    },
    {
      id: 'connector',
      title: 'Chain Connector',
      description: 'Connect blocks in the correct sequence',
      icon: Link,
      path: '/game/connector',
      color: 'from-cyan-500/20 to-transparent'
    },
    {
      id: 'audit',
      title: '🔍 Smart Contract Auditor',
      description: 'Review smart contracts for vulnerabilities and security flaws',
      icon: Bug,
      path: '/game/audit',
      color: 'from-red-600/20 to-transparent',
      isNew: true
    },
    {
      id: 'race',
      title: '🏁 Consensus Race',
      description: 'Watch consensus mechanisms compete in real-time',
      icon: Zap,
      path: '/game/race',
      color: 'from-yellow-600/20 to-transparent',
      isNew: true
    },
    {
      id: 'defi-calc',
      title: '📊 DeFi Calculator',
      description: 'Calculate yields, fees, slippage, and staking rewards',
      icon: Calculator,
      path: '/game/defi-calculator',
      color: 'from-green-600/20 to-transparent',
      isNew: true
    },
    {
      id: 'security-challenge',
      title: '🔐 Wallet Security Challenge',
      description: 'Test your knowledge on wallet security best practices',
      icon: Shield,
      path: '/game/security-challenge',
      color: 'from-blue-600/20 to-transparent',
      isNew: true
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          data-testid="back-to-dashboard-button"
          className="mb-8 text-zinc-400 hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-12" data-testid="games-header">
          <span className="text-xs font-mono uppercase tracking-widest text-primary/80">Game Center</span>
          <h1 className="text-4xl md:text-5xl font-grotesk font-bold text-white mt-2 mb-4 tracking-tight">
            Choose Your <span className="text-primary">Challenge</span>
          </h1>
          <p className="text-zinc-400">Test your blockchain knowledge through interactive games</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {games.map((game) => {
            const IconComponent = game.icon;
            return (
              <button
                key={game.id}
                onClick={() => navigate(game.path)}
                data-testid={`game-${game.id}-button`}
                className="bg-zinc-900/30 border border-white/10 rounded-xl p-6 hover:border-primary/30 hover:-translate-y-1 hover:shadow-2xl hover:shadow-primary/10 text-left group relative overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${game.color} opacity-0 group-hover:opacity-100 transition-opacity`} />
                <div className="relative z-10">
                  {game.isNew && (
                    <div className="mb-3">
                      <span className="inline-block px-2 py-1 text-xs font-bold bg-green-500/20 text-green-300 border border-green-400/30 rounded">
                        ✨ NEW
                      </span>
                    </div>
                  )}
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary/20">
                    <IconComponent className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-grotesk font-semibold text-white group-hover:text-primary mb-2">
                    {game.title}
                  </h3>
                  <p className="text-sm text-zinc-400">{game.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Games;
